import React, {useEffect, useState} from 'react';
import {Send, Users, CheckCircle, Heart, Lightbulb} from 'lucide-react';

/**
 * Middle Stage Peer Assessment component with modern UI design
 * Matches the style of LessonPlanHistory and AssessmentManagementPanel
 */
const MiddleStagePeerAssessment = ({
                                       assignmentId,
                                       submission,
                                       onSubmitComplete,
                                       loading,
                                       setLoading
                                   }) => {
    // Define the response options matching FourOptionResponse enum
    const responseOptions = [
        {value: 'Yes', label: 'Yes', emoji: '😊👍'},
        {value: 'To an extent', label: 'To an extent', emoji: '😐👉'},
        {value: 'No', label: 'No', emoji: '😔👎'},
        {value: 'Not sure', label: 'Not sure', emoji: '🤔'}
    ];

    // Define the grid categories and statements mapping to backend enums
    // The id should match the actual enum VALUE (text string), not the enum key
    const progressGrid = {
        awareness: [
            {id: 'My peer learnt something new.', text: 'My peer learnt something new.'},
            {id: 'My peer understood the activity.', text: 'My peer understood the activity.'},
            {id: 'My peer followed the instructions.', text: 'My peer followed the instructions.'},
            {
                id: 'My peer was attentive to every detail of the activity.',
                text: 'My peer was attentive to every detail of the activity.'
            },
            {id: 'My peer was able to focus on the activity.', text: 'My peer was able to focus on the activity.'},
            {id: 'My peer found this activity meaningful.', text: 'My peer found this activity meaningful.'}
        ],
        sensitivity: [
            {id: 'My peer can express his/her emotions well.', text: 'My peer can express his/her emotions well.'},
            {id: 'My peer can understand my emotions well.', text: 'My peer can understand my emotions well.'},
            {
                id: 'My peer contributed to the success of the activity.',
                text: 'My peer contributed to the success of the activity.'
            },
            {
                id: 'My peer was motivated throughout the activity.',
                text: 'My peer was motivated throughout the activity.'
            },
            {
                id: 'My peer was able to ask for help/support from me or the teacher.',
                text: 'My peer was able to ask for help/support from me or the teacher.'
            },
            {id: 'My peer was able to help others in some way.', text: 'My peer was able to help others in some way.'}
        ],
        creativity: [
            {id: 'My peer was curious to learn new things.', text: 'My peer was curious to learn new things.'},
            {
                id: "My peer was able to think of 'out of the box' solutions.",
                text: "My peer was able to think of 'out of the box' solutions."
            },
            {
                id: 'My peer was able to express his/her creativity during the activity.',
                text: 'My peer was able to express his/her creativity during the activity.'
            },
            {
                id: 'My peer was able to generate innovative ideas.',
                text: 'My peer was able to generate innovative ideas.'
            },
            {id: 'My peer was able to take calculated risks.', text: 'My peer was able to take calculated risks.'}
        ]
    };

    // Initialize form state
    const [form, setForm] = useState({
        // Experience section
        my_name: '',
        peer_name: '',
        engaged_and_motivated: null,
        shared_thoughts: null,
        // Grid selections
        awareness_selections: [],
        sensitivity_selections: [],
        creativity_selections: [],
        // Peer needs
        needs_practice: '',
        needs_help: ''
    });

    // Initialize from existing submission if available
    useEffect(() => {
        if (submission?.peer_assessment) {
            try {
                const peerAssessment = submission.peer_assessment;
                setForm({
                    my_name: peerAssessment.experience?.my_name || '',
                    peer_name: peerAssessment.experience?.peer_name || '',
                    engaged_and_motivated: peerAssessment.experience?.engaged_and_motivated || null,
                    shared_thoughts: peerAssessment.experience?.shared_thoughts || null,
                    awareness_selections: peerAssessment.progress_grid?.awareness || [],
                    sensitivity_selections: peerAssessment.progress_grid?.sensitivity || [],
                    creativity_selections: peerAssessment.progress_grid?.creativity || [],
                    needs_practice: peerAssessment.learnings?.needs_practice || '',
                    needs_help: peerAssessment.learnings?.needs_help || ''
                });
            } catch (error) {
                console.error("Error parsing existing peer assessment:", error);
            }
        }
    }, [submission]);

    // Handle name input changes
    const handleNameChange = (field, value) => {
        setForm(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Handle engagement option selection
    const handleEngagementChange = (questionKey, value) => {
        setForm(prev => ({
            ...prev,
            [questionKey]: value
        }));
    };

    // Handle grid statement selection (toggle)
    const handleGridSelectionChange = (category, itemId) => {
        const selectionKey = `${category}_selections`;
        setForm(prev => {
            const currentSelections = [...prev[selectionKey]];
            const index = currentSelections.indexOf(itemId);

            if (index === -1) {
                currentSelections.push(itemId);
            } else {
                currentSelections.splice(index, 1);
            }

            return {
                ...prev,
                [selectionKey]: currentSelections
            };
        });
    };

    // Handle text input changes
    const handleTextChange = (field, value) => {
        setForm(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Convert form data to API format that matches PeerReflectionPackage
    const prepareFormData = () => {
        return {
            experience: {
                my_name: form.my_name.trim(),
                peer_name: form.peer_name.trim(),
                engaged_and_motivated: form.engaged_and_motivated,
                shared_thoughts: form.shared_thoughts
            },
            progress_grid: {
                awareness: form.awareness_selections,
                sensitivity: form.sensitivity_selections,
                creativity: form.creativity_selections
            },
            learnings: {
                needs_practice: form.needs_practice.trim(),
                needs_help: form.needs_help.trim()
            }
        };
    };

    // Submit the form
    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate required fields
        if (!form.peer_name.trim()) {
            alert("Please enter your peer's name");
            return;
        }

        // Import API service
        const {getApiServiceForGrade} = require('../../utils/gradeUtils');
        const currentApiService = getApiServiceForGrade(6); // Assuming middle grade

        if (setLoading) setLoading(true);

        try {
            const formData = prepareFormData();
            console.log('Submitting peer assessment data:', formData); // Debug log
            const result = await currentApiService.addPeerAssessment(assignmentId, formData);

            if (onSubmitComplete) {
                onSubmitComplete(result);
            }
        } catch (error) {
            console.error("Error submitting peer assessment:", error);
            alert(`Failed to submit: ${error.message}`);
        } finally {
            if (setLoading) setLoading(false);
        }
    };

    // Check if the form has been submitted (properly check for meaningful data)
    const isSubmitted = submission?.peer_assessment &&
        Object.keys(submission.peer_assessment).length > 0 &&
        submission.peer_assessment.experience;

    // Determine if form is disabled (already submitted or loading)
    const isDisabled = isSubmitted || loading;

    return (
        <div className="bg-white font-[poppins] rounded-2xl shadow-md overflow-hidden border border-gray-200 mb-8">
            {/* Header */}
            <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-purple-50 to-pink-50">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-[#D67C2C] shadow-lg">
                        <Users className="w-6 h-6 text-white"/>
                    </div>
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900">Peer Feedback</h2>
                        <p className="text-gray-600 mt-1">Evaluate your peer's performance and engagement</p>
                    </div>
                </div>
            </div>

            <div className="p-8">
                <form onSubmit={handleSubmit}>
                    {/* Name Fields */}
                    <div className="bg-purple-50 rounded-xl p-4 mb-8 border border-purple-200">
                        <p className="text-sm font-medium text-purple-800 mb-4">Participant Information</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label htmlFor="my_name" className="block text-sm font-medium text-gray-700 mb-2">
                                    My name is
                                </label>
                                <input
                                    type="text"
                                    id="my_name"
                                    className="shadow-sm focus:ring-2 focus:ring-[#D67C2C] focus:border-[#D67C2C] block w-full sm:text-sm border-gray-300 rounded-xl"
                                    value={form.my_name}
                                    onChange={(e) => handleNameChange('my_name', e.target.value)}
                                    disabled={isDisabled}
                                    placeholder="Enter your name"
                                />
                            </div>

                            <div>
                                <label htmlFor="peer_name" className="block text-sm font-medium text-gray-700 mb-2">
                                    My peer's name is
                                </label>
                                <input
                                    type="text"
                                    id="peer_name"
                                    className="shadow-sm focus:ring-2 focus:ring-[#D67C2C] focus:border-[#D67C2C] block w-full sm:text-sm border-gray-300 rounded-xl"
                                    value={form.peer_name}
                                    onChange={(e) => handleNameChange('peer_name', e.target.value)}
                                    disabled={isDisabled}
                                    required
                                    placeholder="Enter your peer's name"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Engagement Questions Section */}
                    <div className="mb-8">
                        <div className="bg-blue-50 rounded-xl p-4 mb-6 border border-blue-200">
                            <p className="text-sm font-medium text-blue-800 mb-2">Engagement Assessment</p>
                            <p className="text-sm text-blue-700">
                                Based on your experience of the activity, please select the response that best applies to your peer.
                            </p>
                        </div>

                        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Statement</th>
                                        <th className="px-6 py-4 text-center text-sm font-medium text-gray-900" colSpan="4">Your Assessment</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {/* Engagement 1 */}
                                    <tr className="hover:bg-gray-50">
                                        <td className="px-6 py-4 text-sm text-gray-900 w-2/5">
                                            My peer was engaged and motivated during the activity.
                                        </td>
                                        {responseOptions.map((option) => (
                                            <td key={option.value} className="px-4 py-4 text-center">
                                                <div className="flex flex-col items-center">
                                                    <div className={`text-2xl mb-2 ${isDisabled ? 'opacity-50' : ''}`}>
                                                        {option.emoji}
                                                    </div>
                                                    <label className="inline-flex items-center">
                                                        <input
                                                            type="radio"
                                                            name="engaged_and_motivated"
                                                            value={option.value}
                                                            checked={form.engaged_and_motivated === option.value}
                                                            onChange={() => handleEngagementChange('engaged_and_motivated', option.value)}
                                                            className="form-radio h-4 w-4 text-[#D67C2C] focus:ring-[#D67C2C]"
                                                            disabled={isDisabled}
                                                        />
                                                        <span className="ml-2 text-xs font-medium text-gray-700">{option.label}</span>
                                                    </label>
                                                </div>
                                            </td>
                                        ))}
                                    </tr>

                                    {/* Engagement 2 */}
                                    <tr className="hover:bg-gray-50">
                                        <td className="px-6 py-4 text-sm text-gray-900">
                                            My peer effectively shared thoughts and ideas during the activity.
                                        </td>
                                        {responseOptions.map((option) => (
                                            <td key={option.value} className="px-4 py-4 text-center">
                                                <div className="flex flex-col items-center">
                                                    <div className={`text-2xl mb-2 ${isDisabled ? 'opacity-50' : ''}`}>
                                                        {option.emoji}
                                                    </div>
                                                    <label className="inline-flex items-center">
                                                        <input
                                                            type="radio"
                                                            name="shared_thoughts"
                                                            value={option.value}
                                                            checked={form.shared_thoughts === option.value}
                                                            onChange={() => handleEngagementChange('shared_thoughts', option.value)}
                                                            className="form-radio h-4 w-4 text-[#D67C2C] focus:ring-[#D67C2C]"
                                                            disabled={isDisabled}
                                                        />
                                                        <span className="ml-2 text-xs font-medium text-gray-700">{option.label}</span>
                                                    </label>
                                                </div>
                                            </td>
                                        ))}
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Peer Progress Grid Section */}
                    <div className="mb-8">
                        <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl p-4 mb-6 border border-orange-200">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-[#D67C2C] flex items-center justify-center shadow-lg">
                                    <CheckCircle className="w-5 h-5 text-white"/>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900">My Peer's Progress Grid</h3>
                                    <p className="text-sm text-gray-600">Select statements that are true for your peer's performance</p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                <tr className="divide-x divide-gray-200">
                                    <th scope="col"
                                        className="px-4 py-3 text-center text-sm font-medium text-gray-900 bg-pink-100 w-1/3">
                                        Awareness
                                    </th>
                                    <th scope="col"
                                        className="px-4 py-3 text-center text-sm font-medium text-gray-900 bg-green-100 w-1/3">
                                        Sensitivity
                                    </th>
                                    <th scope="col"
                                        className="px-4 py-3 text-center text-sm font-medium text-gray-900 bg-blue-100 w-1/3">
                                        Creativity
                                    </th>
                                </tr>
                                </thead>

                                <tbody>
                                {/* Determine the maximum number of rows needed */}
                                {Array.from({
                                    length: Math.max(
                                        progressGrid.awareness.length,
                                        progressGrid.sensitivity.length,
                                        progressGrid.creativity.length
                                    )
                                }).map((_, rowIndex) => (
                                    <tr key={rowIndex}
                                        className={`divide-x divide-gray-200 ${rowIndex % 2 === 1 ? 'bg-gray-50' : ''}`}>
                                        <td className="px-4 py-2">
                                            {progressGrid.awareness[rowIndex] ? (
                                                <label className="flex items-start">
                                                    <input
                                                        type="checkbox"
                                                        checked={form.awareness_selections.includes(progressGrid.awareness[rowIndex].id)}
                                                        onChange={() => handleGridSelectionChange('awareness', progressGrid.awareness[rowIndex].id)}
                                                        className="form-checkbox h-4 w-4 mt-1 text-indigo-600"
                                                        disabled={isDisabled}
                                                    />
                                                    <span
                                                        className="ml-2 text-xs">{progressGrid.awareness[rowIndex].text}</span>
                                                </label>
                                            ) : <div className="h-6"></div>}
                                        </td>
                                        <td className="px-4 py-2">
                                            {progressGrid.sensitivity[rowIndex] ? (
                                                <label className="flex items-start">
                                                    <input
                                                        type="checkbox"
                                                        checked={form.sensitivity_selections.includes(progressGrid.sensitivity[rowIndex].id)}
                                                        onChange={() => handleGridSelectionChange('sensitivity', progressGrid.sensitivity[rowIndex].id)}
                                                        className="form-checkbox h-4 w-4 mt-1 text-indigo-600"
                                                        disabled={isDisabled}
                                                    />
                                                    <span
                                                        className="ml-2 text-xs">{progressGrid.sensitivity[rowIndex].text}</span>
                                                </label>
                                            ) : <div className="h-6"></div>}
                                        </td>
                                        <td className="px-4 py-2">
                                            {progressGrid.creativity[rowIndex] ? (
                                                <label className="flex items-start">
                                                    <input
                                                        type="checkbox"
                                                        checked={form.creativity_selections.includes(progressGrid.creativity[rowIndex].id)}
                                                        onChange={() => handleGridSelectionChange('creativity', progressGrid.creativity[rowIndex].id)}
                                                        className="form-checkbox h-4 w-4 mt-1 text-indigo-600"
                                                        disabled={isDisabled}
                                                    />
                                                    <span
                                                        className="ml-2 text-xs">{progressGrid.creativity[rowIndex].text}</span>
                                                </label>
                                            ) : <div className="h-6"></div>}
                                        </td>
                                    </tr>
                                ))}

                                {/* Teacher Use Only Row */}
                                <tr className="divide-x divide-gray-200">
                                    <td colSpan="3" className="px-4 py-2 text-sm font-medium text-gray-700 text-center">
                                        (For Teacher's Use Only)
                                    </td>
                                </tr>
                                <tr className="divide-x divide-gray-200 bg-gray-50">
                                    <td className="px-4 py-2 text-sm">
                                        No. of statements circled for Awareness: <span
                                        className="font-medium">{form.awareness_selections.length}</span>
                                    </td>
                                    <td className="px-4 py-2 text-sm">
                                        No. of statements circled for Sensitivity: <span
                                        className="font-medium">{form.sensitivity_selections.length}</span>
                                    </td>
                                    <td className="px-4 py-2 text-sm">
                                        No. of statements circled for Creativity: <span
                                        className="font-medium">{form.creativity_selections.length}</span>
                                    </td>
                                </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Peer Needs Section */}
                    <div className="mb-8">
                        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 mb-6 border border-green-200">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-green-500 flex items-center justify-center shadow-lg">
                                    <Lightbulb className="w-5 h-5 text-white"/>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900">Peer Development Areas</h3>
                                    <p className="text-sm text-gray-600">Identify areas where your peer could improve</p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label htmlFor="needs_practice"
                                           className="block text-sm font-medium text-gray-700 mb-2">
                                        My peer needs to practice
                                    </label>
                                    <input
                                        type="text"
                                        id="needs_practice"
                                        className="shadow-sm focus:ring-2 focus:ring-[#D67C2C] focus:border-[#D67C2C] block w-full sm:text-sm border-gray-300 rounded-xl"
                                        value={form.needs_practice}
                                        onChange={(e) => handleTextChange('needs_practice', e.target.value)}
                                        disabled={isDisabled}
                                        placeholder="What skills should your peer practice more?"
                                    />
                                </div>

                                <div>
                                    <label htmlFor="needs_help" className="block text-sm font-medium text-gray-700 mb-2">
                                        My peer needs help with
                                    </label>
                                    <input
                                        type="text"
                                        id="needs_help"
                                        className="shadow-sm focus:ring-2 focus:ring-[#D67C2C] focus:border-[#D67C2C] block w-full sm:text-sm border-gray-300 rounded-xl"
                                        value={form.needs_help}
                                        onChange={(e) => handleTextChange('needs_help', e.target.value)}
                                        disabled={isDisabled}
                                        placeholder="What does your peer need support with?"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Teacher Instructions Section */}
                    <div className="mb-6 bg-orange-100 p-4 rounded-md">
                        <h3 className="font-medium text-gray-900 text-center mb-3">
                            How to develop a Holistic Progress Summary? (for teacher's use only)
                        </h3>
                        <p className="text-sm text-gray-700 mb-4">
                            Before filling out the Teacher's Feedback Form, kindly calculate the score for the 3
                            abilities being assessed by the
                            Student's Self Reflection sheet and the Peer Feedback sheet. For each ability i.e. A
                            (Awareness), S (Sensitivity)
                            and C (Creativity), six sub-factors are being assessed (refer appendix). Calculate the
                            number of statements being
                            circled by the student in the self-reflection sheet and by the peer in the peer
                            feedback form. Use the scoring key provided here to assess the student's level of
                            performance on each ability. Indicate the level of performance as reflected on the
                            student and peer assessment sheet in the Student Progress Wheel on the
                            teacher's feedback form.
                        </p>
                        <div className="bg-white p-3 rounded-md border border-gray-300">
                            <h4 className="font-medium text-gray-800 text-center mb-2">Scoring Key:</h4>
                            <p className="text-sm text-gray-700">
                                Beginner – 0,1, 2<br/>
                                Proficient – 3, 4<br/>
                                Advanced – 5, 6
                            </p>
                        </div>
                    </div>

                    {/* Submit Button */}
                    <div className="flex justify-center mt-8 pt-6 border-t border-gray-200">
                        {isSubmitted ? (
                            <div className="inline-flex items-center px-8 py-3 border border-transparent text-base font-medium rounded-xl shadow-lg text-white bg-green-600">
                                <CheckCircle size={20} className="mr-3"/>
                                Assessment Submitted ✓
                            </div>
                        ) : (
                            <button
                                type="submit"
                                className={`inline-flex items-center px-8 py-3 border border-transparent text-base font-medium rounded-xl shadow-lg text-white transition-all hover:scale-105 ${
                                    loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-[#D67C2C] hover:bg-[#c06a25] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#D67C2C]'
                                }`}
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Submitting...
                                    </>
                                ) : (
                                    <>
                                        <Send size={20} className="mr-3"/>
                                        Submit Peer Assessment
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
};

export default MiddleStagePeerAssessment;